import React, { useState } from "react"
import { SearchIcon } from "lucide-react"
import { Communication } from "../../api/communications"

interface PortfolioCompaniesTabProps {
  communicationId: string
  selectedPortfolioCompanies: string[]
  onPortfolioCompanySelectionChange: (selectedIds: string[]) => void
  communication: Communication | undefined
  isLoading: boolean
  error: Error | null
}

const PortfolioCompaniesTab: React.FC<PortfolioCompaniesTabProps> = ({
  communicationId,
  selectedPortfolioCompanies,
  onPortfolioCompanySelectionChange,
  communication,
  isLoading,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState("")

  // Aggregate portfolio companies from all snapshots
  const portfolioCompanies = React.useMemo(() => {
    if (!communication?.snapshots) return []

    // Flatten all portfolio companies from all snapshots
    const allCompanies = communication.snapshots.flatMap(
      (snapshot) => snapshot.portfolio_companies || [],
    )

    // Remove duplicates by creating a map with company ID as key
    const uniqueCompaniesMap = new Map()
    allCompanies.forEach((company) => {
      if (!uniqueCompaniesMap.has(company.id)) {
        uniqueCompaniesMap.set(company.id, company)
      }
    })

    return Array.from(uniqueCompaniesMap.values())
  }, [communication?.snapshots])

  const filteredPortfolioCompanies = portfolioCompanies
    ? portfolioCompanies.filter((company) =>
        company.legal_name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  const handlePortfolioCompanyToggle = (id: string) => {
    if (selectedPortfolioCompanies.includes(id)) {
      onPortfolioCompanySelectionChange(
        selectedPortfolioCompanies.filter((pcId) => pcId !== id),
      )
    } else {
      onPortfolioCompanySelectionChange([...selectedPortfolioCompanies, id])
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = filteredPortfolioCompanies.map((company) => company.id)

      onPortfolioCompanySelectionChange(allIds)
    } else {
      onPortfolioCompanySelectionChange([])
    }
  }

  const areAllSelected =
    filteredPortfolioCompanies.length > 0 &&
    filteredPortfolioCompanies.every((company) =>
      selectedPortfolioCompanies.includes(company.id),
    )

  return (
    <div className="portfolio-companies-tab">
      <h2 className="tab-heading">Portfolio Companies to Exclude</h2>
      <p className="tab-subtext">
        Select the portfolio companies you want to <strong>exclude</strong> from
        the written report if any:
      </p>

      <div className="search-container">
        <label htmlFor="portfolioCompanySearch">
          Search Portfolio Companies
        </label>
        <div className="search-input-wrapper">
          <SearchIcon size={18} className="search-icon" />
          <input
            id="portfolioCompanySearch"
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="selected-count">
        {selectedPortfolioCompanies.length} portfolio companies selected
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading portfolio companies...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="text-danger">
            Error loading portfolio companies. Please try again.
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="portfolio-companies-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={areAllSelected}
                    onChange={handleSelectAll}
                    disabled={filteredPortfolioCompanies.length === 0}
                  />
                </th>
                <th>Exclude All</th>
              </tr>
            </thead>
            <tbody>
              {filteredPortfolioCompanies.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center">
                    {searchTerm
                      ? "No portfolio companies match your search"
                      : "No portfolio companies available"}
                  </td>
                </tr>
              ) : (
                filteredPortfolioCompanies.map((company) => (
                  <tr
                    key={company.id}
                    className={
                      selectedPortfolioCompanies.includes(company.id)
                        ? "selected"
                        : ""
                    }
                  >
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedPortfolioCompanies.includes(
                          company.id,
                        )}
                        onChange={() =>
                          handlePortfolioCompanyToggle(company.id)
                        }
                      />
                    </td>
                    <td>{company.legal_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default PortfolioCompaniesTab
